/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import { neverThrow } from '../../util.type';
import FileSystemPathFactory from '../file/system/file-system-path-factory';
import FileSystemType from '../file/system/file-system-type';
import LogicalUnit from '../logical-unit/logical-unit';

/**
 * @import FileSystemPath from '../file/system/file-system-path'
 * @import { LogicalUnitMapper } from '../logical-unit-mapper/'
 */

/**
 * Resolver pro interní cestu.
 * @typedef InternalBasePath
 * @property {(logicalUnit: LogicalUnit) => Promise<FileSystemPath>} resolve
 * Vrátí basepath pro platformově specifickou interní jednotku.
 */

// TODO: Bug, třída se nejmenuje podle názvu souboru!
/**
 * Třída pro získání a práci s hlavní ukládací cestou BasePath.
 */
export default class BasePathResolver {
  /**
   * Vytvoří instanci.
   * @param {InternalBasePath} internalDownloadPath Zdroj dat pro vyhledání fyzických jednotek.
   * @param {LogicalUnitMapper} logicalUnitMapper Mapovač logických jednotek.
   */
  constructor(internalDownloadPath, logicalUnitMapper) {
    this.internalDownloadPath = internalDownloadPath;
    this.logicalUnitMapper = logicalUnitMapper;
  }

  // TODO: Tato metoda má být jinde, protože třída se jmenuje BasePath(Resolver),
  // ale zde se řeší celé cesty a nikoliv jen nějaké base prefixy!
  /**
   * Vrátí fyzickou FileSystemPath pro danou logickou FileSystemPath.
   * @param {FileSystemPath} logicalPath
   * @returns {Promise<FileSystemPath, Error>} Vyřeší se fyzickou FileSystemPath
   * nebo odmítne chybou při nemožnosti namapování na fyzickou jednotku.
   */
  async toPhysicalPath(logicalPath) {
    const logicalUnitName = logicalPath.getUnit();
    const fileSystemUnit = await this.logicalUnitMapper.toFileSystemUnit(
      new LogicalUnit(logicalUnitName)
    );
    // Zkopírování cesty a nahrazení unit z logické na fyzickou.
    const physicalPath = FileSystemPathFactory.fromInfo({
      base: logicalPath.getBase(),
      extension: logicalPath.getExtension(),
      parents: logicalPath.getParents(),
      stem: logicalPath.getStem(),
      unit: fileSystemUnit.getName()
    });
    return physicalPath;
  }

  // TODO: Název je nevhodně, kde z něho neplyne, že vrací logickou FileSystemPath
  // a ještě pro ukládání assetů!
  /**
   * Vrátí adresář pro ukládání assetů jako logickou FileSystemPath pro danou logickou jednotku.
   * @param {LogicalUnit} logicalUnit
   * @returns {Promise<FileSystemPath, Error>} Vyřeší se logickou FileSystemPath
   * nebo odmítne chybou.
   */
  async resolve(logicalUnit) {
    const fileSystemUnit = await this.logicalUnitMapper.toFileSystemUnit(
      logicalUnit
    );
    const type = fileSystemUnit.getType();
    switch (type) {
      case FileSystemType.Internal:
        return this.internalDownloadPath.resolve(logicalUnit);
      case FileSystemType.Usb:
      case FileSystemType.SdCard:
        return FileSystemPathFactory.fromValue(
          `/${logicalUnit.getName()}/player-lighter/asset`
        );
      default:
        neverThrow(type);
    }
  }
}
